import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";

export type ActivityRowVariant = "success" | "info" | "danger" | "neutral";

type ActivityRowProps = {
  icon: LucideIcon;
  variant?: ActivityRowVariant;
  title: string;
  meta?: string;
  duration?: string;
  timestamp: string;
  onClick?: () => void;
};

const VARIANT_DOT: Record<ActivityRowVariant, string> = {
  success: "bg-status-success",
  info: "bg-status-info",
  danger: "bg-status-danger",
  neutral: "bg-status-neutral",
};

export function ActivityRow({
  variant = "neutral",
  title,
  meta,
  duration,
  timestamp,
  onClick,
}: ActivityRowProps) {
  const Component = onClick ? "button" : "div";

  return (
    <Component
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 border-b border-hairline-soft py-3 text-left last:border-b-0",
        onClick ? "transition-colors hover:bg-muted/40" : null,
      )}
    >
      <span
        className={cn("mt-0.5 h-2 w-2 shrink-0 rounded-full", VARIANT_DOT[variant])}
        aria-hidden="true"
      />

      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-ink">{title}</p>
        {meta ? <p className="truncate text-[12px] text-dim">{meta}</p> : null}
      </div>

      {duration ? (
        <span className="shrink-0 font-mono text-[12px] text-dim">{duration}</span>
      ) : null}
      <span className="shrink-0 text-[11px] text-faint">{timestamp}</span>
    </Component>
  );
}
